<template>
  <div class="information-management">
    <el-card class="information-card">
      <div slot="header" class="card-header">
        <span>资讯管理</span>
      </div>

      <!-- 筛选区域 -->
      <div class="filter-container">
        <el-row :gutter="20">
          <el-col :span="8">
            <el-input v-model="filterForm.title" placeholder="输入资讯标题"></el-input>
          </el-col>
          <el-col :span="16">
            <el-button type="primary" @click="fetchInformations">查询</el-button>
            <el-button @click="resetFilter">重置</el-button>
            <el-button type="primary" @click="showAddInformationDialog">添加资讯</el-button>
          </el-col>
        </el-row>
      </div>

      <!-- 资讯表格 -->
      <el-table
        v-loading="loading"
        :data="informations"
        border
        style="width: 100%"
        class="informations-table"
      >
        <el-table-column prop="resourceId" label="资源ID" width="80"></el-table-column>
        <el-table-column prop="title" label="标题" min-width="150"></el-table-column>
        <el-table-column prop="content" label="简要内容" min-width="250">
          <template #default="{ row }">
            {{ row.content && row.content.length > 50 ? row.content.substring(0, 50) + '...' : row.content || '无内容' }}
          </template>
        </el-table-column>
        <el-table-column prop="type" label="类型" min-width="100">
          <template #default="{ row }">
            <el-tag :type="getTypeType(row.type)">
              {{ getTypeText(row.type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="filePath" label="文件路径" min-width="150">
          <template #default="{ row }">
            <span v-if="row.filePath" class="file-path-text">
              {{ row.filePath.split('/').pop() }}
            </span>
            <span v-else class="no-file-text">无文件</span>
          </template>
        </el-table-column>
        <el-table-column prop="publishTime" label="发布时间" min-width="140">
          <template #default="{ row }">
            {{ formatDate(row.publishTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" min-width="120">
          <template #default="scope">
            <el-button type="text" @click="showEditInformationDialog(scope.row)">编辑</el-button>
            <el-button type="text" danger @click="deleteInformation(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50]"
          :page-size="pageSize"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 添加/编辑资讯对话框 -->
    <el-dialog v-model="informationDialogVisible" :title="isEdit ? '编辑资讯' : '添加资讯'" width="800px">
      <el-form
        ref="informationFormRef"
        :model="informationForm"
        :rules="informationRules"
        label-width="100px"
      >
        <el-form-item label="资讯标题" prop="title">
          <el-input v-model="informationForm.title" placeholder="请输入资讯标题"></el-input>
        </el-form-item>
        <el-form-item label="资讯类型" prop="type">
          <el-select v-model="informationForm.type" placeholder="请选择资讯类型">
              <el-option value="通知" label="通知"></el-option>
              <el-option value="文档" label="文档"></el-option>
            </el-select>
        </el-form-item>

        <el-form-item label="PDF文件">
          <el-upload
            class="file-uploader"
            :auto-upload="false"
            :on-change="handleFileChange"
            :before-upload="beforePdfUpload"
            accept=".pdf"
            :show-file-list="true"
          >
            <el-button type="primary">选择PDF文件</el-button>
            <template #tip>
              <div class="el-upload__tip">
                只能上传PDF文件，且不超过10MB
              </div>
            </template>
          </el-upload>
          <div v-if="informationForm.filePath" class="file-path">
            已上传文件: {{ informationForm.filePath.split('/').pop() }}
          </div>
        </el-form-item>
        <el-form-item label="简要内容" prop="content">
          <el-input
            v-model="informationForm.content"
            type="textarea"
            rows="5"
            placeholder="请输入简要内容"
          ></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="closeDialog">取消</el-button>
        <el-button type="primary" @click="submitInformationForm">提交</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import axios from 'axios';

// 加载状态
const loading = ref(false);

// 筛选条件
const filterForm = reactive({
  title: ''
});

// 表格数据
const informations = ref([]);
const total = ref(0);
const currentPage = ref(1);
const pageSize = ref(10);

// 对话框状态
const informationDialogVisible = ref(false);
const isEdit = ref(false);

// 表单引用
const informationFormRef = ref(null);

// 资讯表单
const informationForm = reactive({
  id: '',
  title: '',
  type: '',
  content: '',
  filePath: ''
});

// 资讯表单验证规则
const informationRules = {
  title: [
    { required: true, message: '请输入资讯标题', trigger: 'blur' },
    { min: 2, max: 100, message: '资讯标题长度在 2 到 100 个字符', trigger: 'blur' }
  ],
  type: [
    { required: true, message: '请选择资讯类型', trigger: 'change' }
  ],
  content: [
    { required: true, message: '请输入简要内容', trigger: 'blur' },
    { min: 5, max: 500, message: '简要内容长度在 5 到 500 个字符', trigger: 'blur' }
  ]
};

// 获取资讯类型文本
const getTypeText = (type) => {
  switch (type) {
    case '通知': return '通知';
    case '文档': return '文档';
    default: return type;
  }
};

// 获取资讯类型样式
const getTypeType = (type) => {
  switch (type) {
    case '通知': return 'primary';
    case '文档': return 'info';
    default: return '';
  }
};

// 格式化日期，只显示年月日
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  // 确保日期有效
  if (isNaN(date.getTime())) return dateString;
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

// 获取资讯列表
const fetchInformations = async () => {
  try {
    loading.value = true;
    const params = {
      page: currentPage.value,
      size: pageSize.value,
      title: filterForm.title || undefined
    };

    const response = await axios.get('/admin/informations', { params });
    console.log('后端返回原始数据:', response.data);
    
    // 检查数据结构并确保正确赋值
    if (response.data && response.data.data) {
      // 如果响应有嵌套的data字段
      console.log('使用嵌套data结构');
      informations.value = response.data.data.records || [];
      total.value = response.data.data.total || 0;
    } else {
      // 直接从响应中获取数据
      console.log('使用直接数据结构');
      informations.value = response.data.records || [];
      total.value = response.data.total || 0;
    }
    
    console.log('赋值后表格数据:', informations.value);
    console.log('赋值后总数:', total.value);
  } catch (error) {
    console.error('获取资讯列表失败:', error);
    ElMessage.error('获取资讯列表失败');
  } finally {
    loading.value = false;
  }
};

// 重置筛选
const resetFilter = () => {
  filterForm.title = '';
  currentPage.value = 1;
  fetchInformations();
};

// 分页处理
const handleSizeChange = (size) => {
  pageSize.value = size;
  currentPage.value = 1;
  fetchInformations();
};

const handleCurrentChange = (page) => {
  currentPage.value = page;
  fetchInformations();
};

// 清空表单数据
const resetForm = () => {
  informationForm.id = '';
  informationForm.title = '';
  informationForm.type = '';
  informationForm.content = '';
  informationForm.filePath = '';
  selectedFile.value = null;
  
  // 重置表单验证状态
  if (informationFormRef.value) {
    informationFormRef.value.resetFields();
  }
};

// 关闭对话框
const closeDialog = () => {
  informationDialogVisible.value = false;
  // 在下一个事件循环清空表单，避免关闭动画期间显示空白
  setTimeout(() => {
    resetForm();
  }, 300);
};

// 显示添加资讯对话框
const showAddInformationDialog = () => {
  isEdit.value = false;
  resetForm();
  informationDialogVisible.value = true;
};

// 显示编辑资讯对话框
const showEditInformationDialog = (row) => {
  isEdit.value = true;
  // 填充表单数据
  informationForm.id = row.resourceId;
  informationForm.title = row.title;
  informationForm.type = row.type;
  informationForm.content = row.content;
  informationForm.filePath = row.filePath || '';
  // 重置文件选择
  selectedFile.value = null;
  
  informationDialogVisible.value = true;
};

// 保存选择的PDF文件对象
const selectedFile = ref(null);

// 处理文件选择变化
const handleFileChange = (file, fileList) => {
// 保存选择的文件对象
selectedFile.value = file.raw;
console.log('选择的文件:', selectedFile.value);
};

// PDF文件上传成功处理
const handlePdfUploadSuccess = (response, file, fileList) => {
  if (response.code === 200 || response.success) {
    informationForm.filePath = response.filePath || response.data;
    ElMessage.success('PDF文件上传成功');
  } else {
    ElMessage.error('PDF文件上传失败: ' + (response.message || '未知错误'));
  }
};

// PDF文件上传前验证
const beforePdfUpload = (file) => {
  const isPDF = file.type === 'application/pdf' || file.name.endsWith('.pdf');
  const isLt10M = file.size / 1024 / 1024 < 10;

  if (!isPDF) {
    ElMessage.error('上传文件只能是 PDF 格式!');
  }
  if (!isLt10M) {
    ElMessage.error('上传文件大小不能超过 10MB!');
  }
  return isPDF && isLt10M;
};

// 提交资讯表单
const submitInformationForm = async () => {
  try {
    if (!informationFormRef.value) {
      console.error('表单引用不存在');
      return;
    }
    
    await informationFormRef.value.validate();

    // 创建FormData对象，用于提交multipart/form-data格式数据
    const formData = new FormData();
    formData.append('title', informationForm.title);
    formData.append('type', informationForm.type);
    formData.append('content', informationForm.content);
    
    // 添加文件对象（如果有选择新文件）
    if (selectedFile.value) {
      formData.append('pdfFile', selectedFile.value);
      console.log('添加新文件到FormData');
    }

    console.log('提交数据:', formData);

    // 不要手动设置Content-Type，让浏览器自动处理boundary
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    };

    if (isEdit.value) {
      // 编辑资讯
      const response = await axios.put(`/admin/informations/${informationForm.id}`, formData, config);
      if (response.data.code === 200 || response.data.success) {
        ElMessage.success('资讯编辑成功');
      }
    } else {
      // 添加资讯
      const response = await axios.post('/admin/informations', formData, config);
      if (response.data.code === 200 || response.data.success) {
        ElMessage.success('资讯添加成功');
      }
    }

    closeDialog();
    fetchInformations();
  } catch (error) {
    console.error('提交资讯表单失败:', error);
    if (error === false) {
      // 表单验证失败，不显示错误消息
      return;
    }
    ElMessage.error(isEdit.value ? '资讯编辑失败' : '资讯添加失败');
  }
};

// 删除资讯
const deleteInformation = async (information) => {
  try {
    await ElMessageBox.confirm('确定要删除该资讯吗？', '警告', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    });

    // 使用resourceId进行删除
    const response = await axios.delete(`/admin/informations/${information.resourceId}`);
    if (response.data.code === 200 || response.data.success) {
      ElMessage.success('资讯删除成功');
      fetchInformations();
    } else {
      ElMessage.error('资讯删除失败');
    }
  } catch (error) {
    if (error === 'cancel') return;
    console.error('删除资讯失败:', error);
    ElMessage.error('资讯删除失败');
  }
};

onMounted(() => {
  fetchInformations();
});
</script>

<style scoped>
.information-management {
  padding: 10px 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* 添加资讯按钮样式已移除，使用默认样式 */

.filter-container {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.informations-table {
  margin-bottom: 20px;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 15px;
}



.file-uploader {
  margin-bottom: 10px;
}

.file-path {
  margin-top: 10px;
  padding: 5px;
  background-color: #f0f9ff;
  border: 1px solid #d9ecff;
  border-radius: 4px;
  color: #1890ff;
  font-size: 14px;
  word-break: break-all;
}

.file-path-text {
  color: #1890ff;
  font-size: 14px;
  cursor: pointer;
}

.file-path-text:hover {
  text-decoration: underline;
}

.no-file-text {
  color: #909399;
  font-size: 14px;
}
</style>